/**
 * Types for the Subscription Plans feature.
 *
 * Every name here is the key the API answers with, so a response drops straight
 * into the store with no renaming in between — see
 * `src/redux/actions/subscription.action.ts`.
 *
 *   GET  customer/subscription/list
 *   GET  customer/subscription/details?subscription_id=
 *   POST customer/subscription/cancel
 *   POST customer/subscription/create_setup_intent
 *   POST customer/subscription/add_payment_method
 *   POST customer/subscription/set_payment_method
 *   POST customer/subscription/pay_now
 */

/** Status of a subscription. Mirrors the numeric style of ContractStatus. */
export enum SubscriptionStatus {
  Active = 1,
  Paused = 2,
  Completed = 3,
  Cancelled = 4,
}

/** Where a subscription sits in the cancellation journey. */
export enum CancellationStatus {
  /** Not cancelled, and no request has been raised. */
  None = 0,
  /** Customer asked to cancel, waiting for the business to approve. */
  Pending = 1,
  /** Cancellation went through. The subscription is over. */
  Approved = 2,
}

/** One of the three collapsible sections on the subscription page. */
export enum SubscriptionSection {
  MANAGE = 'manage',
  PAYMENT_METHODS = 'payment_methods',
  TRANSACTION_HISTORY = 'transaction_history',
}

/** How a customer pays from the "How would you like to pay?" page. */
export enum SubscriptionPayMethod {
  EXISTING_CARD = 'existing_card',
}

/**
 * A saved card or bank account.
 *
 * These are the keys `details/get_customer_payment_source` answers with — the
 * same list the dashboard uses, which knows nothing about subscriptions. Which
 * of them a plan bills is read from the plan's own `source_id` and
 * `secondary_source_id`, not from `default` / `backup`: those are computed from
 * a contract, and are 0 for every row when no contract_id is sent.
 */
export interface SubscriptionPaymentMethod {
  source_id: number;
  /** "Visa" | "Mastercard" | "Amex" | "Bank" - capitalised by the server. */
  brand: string;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
  /** 1 is a bank account; the card types are the rest. */
  payment_source_type: number;
  patient_first_name?: string;
  patient_last_name?: string;
  /** Set from the contract, so always 0 on the subscription page. */
  default?: number;
  backup?: number;
}

/** A single row in the Transaction History table. */
export interface SubscriptionTransaction {
  transaction_id: number;
  /** The date the payment was scheduled for. Null on a charge with no period. */
  recurring_date: string | null;
  /** The date the money actually moved. */
  transaction_date: string | null;
  amount: number;
  brand: string;
  last4: string;
  /** "Paid" | "Failed" | "Refunded" | "Pending" */
  status: string;
  /**
   * A partial refund leaves the charge Paid, so `status` alone cannot say one
   * happened. Non-zero means some of this charge went back.
   */
  refunded_amount: number;
  /** The stored status behind the label: 1 pending, 2 paid, 3 failed, 4 refunded. */
  status_code: number | null;
}

/** The business that sells the plan. Shown in the Manage Subscription section. */
export interface SubscriptionProvider {
  provider_name: string;
  provider_email: string;
  provider_mobile: string;
}

/** A subscription as shown in the sidebar list and on the main page. */
export interface Subscription {
  subscription_id: number;
  /** Human readable id, e.g. "AMPT3J742480". Shown as "ID:" on the plan card. */
  subscription_code: string;
  plan_name: string;
  plan_description: string;
  /** Recurring price, charged every `billing_cycle`. */
  amount: number;
  /** Currently only "month" is designed for, but kept open for "year". */
  billing_cycle: 'month' | 'year';
  /**
   * Months between charges, as sold. `billing_cycle` collapses this to the two
   * the page is designed for; a quarterly plan is `month` there and 3 here.
   */
  service_interval: number;
  status: SubscriptionStatus;
  /**
   * The billing status underneath `status`: 1 active, 2 upcoming, 3 overdue,
   * 4 inactive, 5 canceled, 6 paused. `status` folds upcoming and overdue into
   * Active, because neither is something a customer can act on.
   */
  sub_status: number | null;
  cancellation_status: CancellationStatus;
  /** When the customer asked to cancel. Null until they do. */
  cancellation_requested_at: string | null;
  /** ISO date of the next charge. Null once cancelled or finished. */
  next_payment_date: string | null;
  /** Bullet points shown as green chips under "Plan Includes". */
  plan_includes: string[];
  provider: SubscriptionProvider;
  /** Data URL / image URL of the signature captured at sign up. */
  customer_signature: string | null;
  /**
   * When 1, cancelling raises a request that the business must approve first.
   * When 0, cancelling takes effect immediately.
   * This is what decides which of the two cancel modals (and which result
   * screen) the customer sees.
   */
  requires_cancellation_approval: number;
  /**
   * Which saved card this plan bills, and which it falls back to. The cards
   * themselves come from `details/get_customer_payment_source`; these two say
   * which rows in that list are the ones in use.
   */
  source_id: number | null;
  secondary_source_id: number | null;
}

/** What `transactions` answers with. One page of charge history. */
export interface SubscriptionTransactionsResult {
  subscription_id: number;
  transactions: SubscriptionTransaction[];
  /** Across every page, so the panel can say "20 of 34". */
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/** Asking for a page of charge history. Omit both to get the newest 20. */
export interface SubscriptionTransactionsInput {
  subscription_id: number;
  limit?: number;
  offset?: number;
}

/** Payload sent when the customer confirms a cancellation. */
export interface CancelSubscriptionInput {
  subscription_id: number;
  /** Free text from the "Tell us why you're canceling" box. Optional by design. */
  reason?: string;
}

/** What `cancel` answers with. */
export interface CancelSubscriptionResult {
  subscription_id: number;
  cancellation_status: CancellationStatus;
  /** True when it had already been cancelled or already requested. */
  already_finished: boolean;
}

/**
 * Adding a card is two calls with the customer in the middle: mint a
 * SetupIntent, let Stripe.js confirm it, then store what came back.
 *
 * `payment_source_type` is the contract flow's: 1 is a bank account, and the
 * card types are the rest.
 */
export interface CreateSetupIntentInput {
  subscription_id: number;
  payment_source_type?: number;
}

export interface CreateSetupIntentResult {
  subscription_id: number;
  name: string;
  email: string;
  client_secret: string;
  setup_intent_id: string;
  payment_method_type: string[];
}

export interface AddPaymentMethodInput {
  subscription_id: number;
  /** The SetupIntent Stripe.js has just confirmed. */
  setup_id: string;
  payment_source_type?: number;
  /** Cover for the card in use, rather than a replacement for it. */
  is_backup?: boolean;
}

/** Pointing a subscription at a card that is already saved. */
export interface SetPaymentMethodInput {
  subscription_id: number;
  source_id?: number;
  secondary_source_id?: number;
}

export interface SetPaymentMethodResult {
  subscription_id: number;
  source_id: number | null;
  secondary_source_id: number | null;
  /**
   * True when promoting one card pushed the other into the slot it came from.
   * The two can never hold the same card, so a promotion is a swap.
   */
  swapped: boolean;
}

/** Paying the coming period early. Omit `source_id` to use the plan's own card. */
export interface PayNowInput {
  subscription_id: number;
  source_id?: number;
}

/**
 * What `pay_now` answers with.
 *
 * `requires_confirmation: true` means **nothing has been charged yet** — confirm
 * `client_secret` with Stripe.js and the webhook settles it. Without it, a bank
 * charge has already gone through and `charge_id` is set. `already_charged`
 * means this period was paid before the button was pressed.
 */
export interface PayNowResult {
  subscription_id: number;
  requires_confirmation?: boolean;
  client_secret?: string;
  payment_intent_id?: string;
  payment_intent_status?: string;
  source_id?: number;
  payment_source_type?: number;
  amount_to_charge?: number;
  currency_code?: string;
  charge_id?: string;
  amount_charged?: number;
  next_billing_date?: string;
  actual_billing_date?: string;
  already_charged?: boolean;
}
